/**
 * Offline-queue retry classification + backoff.
 *
 * Both pieces are pure functions so they're unit-testable (core has no
 * test runner of its own — these are covered by the web app's jest
 * suite via `@splitfiesta/core`).
 */

/**
 * Classifies a sync failure as **transient** (the server never durably
 * received the write — safe and *necessary* to retry indefinitely) vs
 * **permanent** (the server rejected it — give up after `maxRetries`).
 *
 * - Transient: connectivity/timeout errors (no HTTP status, including a
 *   bare `AbortError` from a client timeout) and HTTP
 *   5xx / 429 (backend down, gateway, overloaded). During a backend
 *   outage every attempt fails this way, so a queued expense must NOT
 *   burn its retry budget and end up stranded as permanently `failed`.
 * - Permanent: HTTP 4xx (validation, auth, conflict) — retrying won't
 *   help.
 * - Unknown shapes default to **permanent** so a genuinely-broken item
 *   can't retry forever; the network/5xx cases we care about during an
 *   outage are matched explicitly.
 *
 * Matches the `ApiError` shape thrown by the app HTTP clients
 * (`{ status?, code?, message? }`) plus raw fetch failures.
 */
export function isTransientSyncError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as {
    name?: string;
    status?: number;
    code?: string;
    message?: string;
  };

  // A client-side timeout aborts the fetch: the request may never have
  // reached the server, and the idempotency key makes a resend safe.
  if (e.name === 'AbortError') return true;
  if (
    e.code === 'OFFLINE' ||
    e.code === 'NETWORK_ERROR' ||
    e.code === 'TIMEOUT'
  ) {
    return true;
  }
  if (
    e.message === 'Failed to fetch' ||
    e.message === 'Network request failed'
  ) {
    return true;
  }
  if (typeof e.status === 'number') {
    return e.status >= 500 || e.status === 429;
  }
  return false;
}

const SYNC_RETRY_BASE_MS = 5_000;
const SYNC_RETRY_MAX_MS = 5 * 60_000; // 5 minutes

/**
 * Exponential backoff (capped) for the offline-queue *flush trigger* —
 * how long to wait before the next flush attempt while items remain
 * unsynced after a transient failure. `attempt` is the number of
 * consecutive flush cycles that left work behind (1-based).
 *
 *   attempt 1 → 5s, 2 → 10s, 3 → 20s, 4 → 40s … capped at 5 min.
 *
 * Deterministic (no jitter) so it's unit-testable; the queue is
 * per-device, so thundering-herd jitter isn't needed.
 */
export function nextSyncRetryDelayMs(attempt: number): number {
  if (attempt <= 1) return SYNC_RETRY_BASE_MS;
  return Math.min(SYNC_RETRY_MAX_MS, SYNC_RETRY_BASE_MS * 2 ** (attempt - 1));
}
